import { z } from 'zod'
import type { UserRecord,  WorkspaceRecord, SubscriptionRecord } from '@taqat/db'

/**
 * Authentication-related types and validation schemas
 * Provides type safety for authentication operations
 */

/**
 * Email validation schema
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Invalid email format')
  .max(255, 'Email too long')
  .refine((email) => !email.includes('+'), {
    message: 'Email aliases with "+" are not allowed',
  })

/**
 * Password validation schema
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(100, 'Password too long')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')

/**
 * Signin request validation schema
 */
export const signinSchema = z.object({
  email: emailSchema,
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  workspaceId: z
    .string()
    .uuid('Invalid workspace ID format')
    .optional(),
})

/**
 * Signin request type
 */
export type SigninRequest = z.infer<typeof signinSchema>

/**
 * Signup form validation schema
 */
export const signupSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name too long')
    .trim(),
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

/**
 * Type inference for signup schema
 */
export type SignupRequest = z.infer<typeof signupSchema>

/**
 * API response types
 */
export interface SignupResponse {
  redirectUrl: string
  status: 'success' | 'error'
  userRecord?: any
  account?: any
  invitation?: any
  message?: string
}

/**
 * Registration schema for internal use
 */
export const registerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: emailSchema,
  password: passwordSchema,
  provider: z.string().default('email'),
  username: z.string().optional(),
})

export type RegisterSchema = z.infer<typeof registerSchema>

/**
 * User session data structure
 */
export interface UserSession {
  sessionToken: string
  userId: string
  expires: Date
  refreshToken?: string
  ipAddress?: string
  userAgent?: string
  // Stored signin response data
  userData?: Omit<UserRecord, 'password' | 'passwordSalt' | 'tempPassword'>
  selectedWorkspace?: WorkspaceRecord | null
  workspaces?: WorkspaceRecord[]
  subscription?: SubscriptionRecord | null
}

/**
 * Complete signin response structure
 */
export interface SigninResponse {
  session: UserSession | null
  user: Omit<UserRecord, 'password' | 'passwordSalt' | 'tempPassword'>
  workspaces: WorkspaceRecord[]
  selectedWorkspace: WorkspaceRecord | null
  subscription: SubscriptionRecord | null
  redirectUrl?: string
}

/**
 * Authentication error types
 */
export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  USER_ALREADY_EXISTS = 'USER_ALREADY_EXISTS',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  ACCOUNT_SUSPENDED = 'ACCOUNT_SUSPENDED',
  INVALID_WORKSPACE = 'INVALID_WORKSPACE',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  RATE_LIMITED = 'RATE_LIMITED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  INVALID_TEMP_PASSWORD = 'INVALID_TEMP_PASSWORD',
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
}

/**
 * Authentication error class
 */
export class AuthError extends Error {
  constructor(
    public type: AuthErrorType,
    message: string,
    public statusCode: number = 401
  ) {
    super(message)
    this.name = 'AuthError'
  }
}

/**
 * JWT payload structure.
 *
 * Identifiers only — deliberately. This used to carry the whole
 * `selectedWorkspace` record, which made token size depend on user-entered
 * content: a workspace with long Super-Agent `additionalInstructions` produced
 * a token over the 2704-byte ceiling of the `sessions_sessionToken_unique`
 * btree index, and every sign-in for that workspace failed with a generic 500.
 *
 * Nothing needed the object. The only consumer outside this module read
 * `.id`, which `workspaceId` already carries, and `getSession` re-reads the
 * workspace from the database regardless. Carrying it also meant workspace
 * settings were frozen for the token's 24-hour life, so a settings change did
 * not apply until the user logged in again.
 *
 * Keep this payload to identifiers. Anything that can grow with user input
 * belongs in the `sessions` row (JSONB, unindexed) or a fresh read.
 */
export interface JWTPayload {
  userId: string
  sessionId: string
  workspaceId?: string
  iat?: number
  exp?: number
}

/**
 * Session creation options
 */
export interface SessionOptions {
  ipAddress?: string
  userAgent?: string
  expiryHours?: number
  // Signin response data to store in session
  userData?: Omit<UserRecord, 'password' | 'passwordSalt' | 'tempPassword'>
  selectedWorkspace?: WorkspaceRecord | null
  workspaces?: WorkspaceRecord[]
  subscription?: SubscriptionRecord | null
}

/**
 * User verification result
 */
export interface UserVerificationResult {
  user: UserRecord
  isValid: boolean
  requiresEmailVerification: boolean
}

/**
 * Forgot password request validation schema
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
})

/**
 * Reset password request validation schema
 */
export const resetPasswordSchema = z.object({
  email: emailSchema,
  tempPassword: z
    .string()
    .min(1, 'Temporary password is required'),
  newPassword: passwordSchema,
})

/**
 * Forgot password request type
 */
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordSchema>

/**
 * Reset password request type
 */
export type ResetPasswordRequest = z.infer<typeof resetPasswordSchema>

/**
 * Forgot password response type
 */
export interface ForgotPasswordResponse {
  success: boolean
  message: string
  tempPasswordId?: string
}

/**
 * Reset password response type
 */
export interface ResetPasswordResponse {
  success: boolean
  message: string
}

/**
 * IP address validation schema
 */
export const ipAddressSchema = z
  .string()
  .regex(
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/,
    'Invalid IP address format'
  )

/**
 * User agent validation schema
 */
export const userAgentSchema = z
  .string()
  .max(500, 'User agent string too long')
  .optional()

/**
 * Session validation schema
 */
export const sessionValidationSchema = z.object({
  sessionToken: z.string().min(1, 'Session token is required'),
  ipAddress: ipAddressSchema.optional(),
  userAgent: userAgentSchema.optional(),
})

/**
 * Refresh token request schema
 */
export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token is required'),
})

/**
 * Middleware context extensions
 * Types for data that authentication middleware adds to Hono context
 */

/**
 * API Key data structure for middleware context
 */
export interface ApiKeyData {
  keyId: string
  name?: string
  permissions?: string[]
  userId?: string
  workspaceId?: string
  rateLimit?: number
  isActive: boolean
}

/**
 * Authentication context variables that middleware adds to Hono context
 * Use these types when accessing authenticated data in route handlers
 */
export interface AuthContextVariables {
  // Session-based authentication (Bearer token)
  session?: UserSession
  user?: Omit<UserRecord, 'password' | 'passwordSalt' | 'tempPassword'>
  workspace?: WorkspaceRecord | null
  workspaces?: WorkspaceRecord[]
  subscription?: SubscriptionRecord | null

  // API Key authentication
  apiKey?: ApiKeyData
  apiKeyId?: string
  apiKeyPermissions?: string[]

  // Workspace-scoped role resolved from user_workspaces + per-agent grants.
  // Populated by the auth middleware after the session is established.
  userRole?: 'admin' | 'user'
  accessibleAgents?: string[]
}

// Note: Module augmentation for Hono should be done in consuming packages (server)
// not in the shared library, to avoid dependency issues

